import type { FC } from "react";
import { useEffect, useMemo, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { BasicModal, Button } from "@similarweb/ui-components";

import I18n from "app/pages/sales-intelligence/common-components/i18n/I18n";
import {
    type InclusionOption,
    inclusionOptionMap,
    type OperatorOption,
    operatorOptionMap,
} from "app/pages/sales-intelligence/types/commonMaps";

import type { IAppsAdSellersFilterItem } from "../../../types/commonFiltersDto";
import type { IAppsAdsSellersFilterDescriptionDto } from "../../../types/descriptionDto";
import { getDisabledAdsSellersValues, hasSameAdsSellersObject } from "../../../utils";
import InclusionRadioButtons from "../../InclusionRadioButtons/InclusionRadioButtons";
import OperatorsDropdown from "../../OperatorsDropdown/OperatorsDropdown";
import { filterModalStyles, getFilterModalCloseIconConfig } from "../configs";
import {
    StyledButtonsContainer,
    StyledContainerWithChips,
    StyledDDContent,
    StyledHeader,
    StyledMainContainer,
    StyledModalContent,
    StyledOperationContainer,
    StyledSubtitle,
    StyledTitle,
} from "../styles";

import AdsSellersChipsGroup from "./AdsSellersChipsGroup";
import AdsSellersDropdown from "./AdsSellersDropdown";
import { StyledAdsSellersDropdownWrapper } from "./styles";

const INIT_ADS_SELLERS_VALUE: IAppsAdSellersFilterItem = {
    inclusion: inclusionOptionMap.includeOnly,
    operator: operatorOptionMap.and,
    adsSellerDomainNames: [],
};

type AdsSellersModalProps = {
    isOpen: boolean;
    selected: IAppsAdSellersFilterItem | null;
    options: IAppsAdsSellersFilterDescriptionDto;
    value: IAppsAdSellersFilterItem[] | null | undefined;
    onApply: (item: IAppsAdSellersFilterItem, selected?: IAppsAdSellersFilterItem | null) => void;
    onClose: VoidFunction;
    onCancel: VoidFunction;
};

const AdsSellersModal: FC<AdsSellersModalProps> = ({
    isOpen,
    selected,
    options,
    value,
    onApply,
    onCancel,
    onClose,
}) => {
    const { translate } = useTranslate();

    const [adsSellersItem, setAdsSellersItem] = useState<IAppsAdSellersFilterItem>(INIT_ADS_SELLERS_VALUE);

    const hasChips = adsSellersItem.adsSellerDomainNames.length > 0;

    const initValue = () => {
        setAdsSellersItem(INIT_ADS_SELLERS_VALUE);
    };

    const handleSetInclusion = (inclusionOption: InclusionOption) => {
        const item = { ...adsSellersItem, inclusion: inclusionOption };
        if (inclusionOption === inclusionOptionMap.excludeOnly) {
            item.operator = operatorOptionMap.or;
        }
        setAdsSellersItem(item);
    };

    const handleCancel = () => {
        onCancel();
        initValue();
    };

    const handleOnApply = () => {
        onApply(adsSellersItem, selected);
        initValue();
    };

    const isApplyDisabled = () => {
        return adsSellersItem.adsSellerDomainNames.length === 0 || hasSameAdsSellersObject(adsSellersItem, selected);
    };

    const handleOnItemClick = (item: string) => {
        const { adsSellerDomainNames } = adsSellersItem;

        const updated = adsSellerDomainNames.includes(item)
            ? adsSellerDomainNames.filter((name) => name !== item)
            : [...adsSellerDomainNames, item];

        setAdsSellersItem((prev) => ({ ...prev, adsSellerDomainNames: updated }));
    };

    const handleChangeOperator = (operator: OperatorOption) => {
        setAdsSellersItem((prev) => ({ ...prev, operator }));
    };

    useEffect(() => {
        if (isOpen && selected) {
            setAdsSellersItem(selected);
        }

        if (!isOpen) {
            initValue();
        }
    }, [isOpen, selected]);

    const isEnabledExclude = useMemo((): boolean => {
        return (
            (selected && selected?.inclusion === inclusionOptionMap.excludeOnly) ||
            !value?.find((item) => item.inclusion === inclusionOptionMap.excludeOnly)
        );
    }, [selected, value]);

    const currentSelectedDomains = getDisabledAdsSellersValues(value, selected, adsSellersItem);

    return (
        <BasicModal
            isOpen={isOpen}
            onRequestClose={onClose}
            customStyles={filterModalStyles}
            closeIconConfig={getFilterModalCloseIconConfig(onClose)}
            dataAutomation="ads-sellers-filter-modal"
        >
            <div>
                <StyledHeader>
                    <I18n
                        translatable={{ key: "si.find_apps.filters.ads_sellers.modal.title" }}
                        element={StyledTitle}
                    />
                    <I18n
                        translatable={{ key: "si.find_apps.filters.ads_sellers.modal.subtitle" }}
                        element={StyledSubtitle}
                    />
                </StyledHeader>
                <StyledModalContent>
                    <StyledMainContainer>
                        <InclusionRadioButtons
                            isExcludeAvailable={isEnabledExclude}
                            selectedInclusion={adsSellersItem.inclusion}
                            onSelect={handleSetInclusion}
                        />
                        <StyledContainerWithChips hasChips={hasChips}>
                            <AdsSellersChipsGroup
                                adsSellers={adsSellersItem}
                                onChange={setAdsSellersItem}
                            />
                            <StyledDDContent>
                                {adsSellersItem.inclusion === inclusionOptionMap.includeOnly ? (
                                    <StyledOperationContainer>
                                        <OperatorsDropdown
                                            options={options.operatorOptions}
                                            value={adsSellersItem.operator}
                                            onChange={handleChangeOperator}
                                        />
                                    </StyledOperationContainer>
                                ) : null}

                                <StyledAdsSellersDropdownWrapper>
                                    <AdsSellersDropdown
                                        onSelect={handleOnItemClick}
                                        items={options.adsSellerDomainNames}
                                        selectedItems={adsSellersItem.adsSellerDomainNames}
                                        disabledItems={currentSelectedDomains}
                                    />
                                </StyledAdsSellersDropdownWrapper>
                            </StyledDDContent>
                        </StyledContainerWithChips>
                    </StyledMainContainer>
                    <StyledButtonsContainer>
                        <Button
                            type="flat"
                            onClick={handleCancel}
                            dataAutomation="ads-sellers-modal-button-cancel"
                        >
                            {translate("si.common.button.cancel")}
                        </Button>
                        <Button
                            onClick={handleOnApply}
                            isDisabled={isApplyDisabled()}
                            dataAutomation="ads-sellers-modal-button-apply"
                        >
                            {translate(`common.apply`)}
                        </Button>
                    </StyledButtonsContainer>
                </StyledModalContent>
            </div>
        </BasicModal>
    );
};

export default AdsSellersModal;
